/**
 * API Configuration
 * 
 * Centralized configuration for API base URL and Stripe publishable key.
 * All services should import from this file instead of duplicating
 * the environment variable logic.
 * 
 * Environment Variables:
 * - VITE_API_URL: The base URL for the API (defaults to http://localhost:3001)
 * - VITE_STRIPE_PUBLISHABLE_KEY: Stripe publishable key for payment processing
 * 
 * Usage:
 * ```typescript
 * import { API_BASE_URL, STRIPE_PUBLISHABLE_KEY } from '../config/api';
 * ```
 */

/**
 * Base URL for the API backend
 *
 * - Local dev: set VITE_API_URL=http://localhost:5001/... in .env
 * - Deployed (Firebase Hosting): set VITE_API_URL= (empty) so requests use relative
 *   paths and Firebase Hosting rewrites proxy them to the Cloud Function.
 * - Fallback when env var is not defined at all: localhost:3001
 */
export const API_BASE_URL: string =
  import.meta.env.VITE_API_URL !== undefined
    ? import.meta.env.VITE_API_URL   // empty string = relative paths (Firebase Hosting rewrites)
    : 'http://localhost:3001';

/**
 * Build a fetch-ready API path with optional query params.
 * Avoids `new URL()` so empty API_BASE_URL (relative Hosting rewrites) stays valid.
 *
 * Do not use `new URL(\`\${API_BASE_URL}...\`)` — ESLint enforces this; empty base throws at runtime.
 */
export function buildApiUrl(
  path: string,
  searchParams?: Record<string, string | undefined>
): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const basePath = `${API_BASE_URL}${normalizedPath}`;

  if (!searchParams) {
    return basePath;
  }

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(searchParams)) {
    if (value !== undefined && value !== '') {
      params.append(key, value);
    }
  }

  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
}

/**
 * Stripe Publishable Key
 * 
 * Used for initializing Stripe.js and processing payments on the frontend.
 * Get your publishable key from: https://dashboard.stripe.com/apikeys
 * 
 * Defaults to undefined if not set (Stripe will fail to initialize).
 * In production, set VITE_STRIPE_PUBLISHABLE_KEY environment variable.
 */
export const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

/**
 * Google Maps API Key
 * 
 * Used for initializing Google Maps components.
 * In production, set VITE_GOOGLE_MAPS_API_KEY environment variable.
 */
export const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

/**
 * Passed to every `useJsApiLoader` that shares {@link GOOGLE_MAPS_LOADER_SCRIPT_ID}.
 * Google’s loader is a singleton; mismatched `libraries` across calls throws at runtime.
 */
export const GOOGLE_MAPS_LOADER_LIBRARIES = ['places', 'marker'] as const;

export const GOOGLE_MAPS_LOADER_SCRIPT_ID = 'google-map-script';
